import React, { useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Check } from "lucide-react";

const BG = {
  examen: ["from-sky-500 to-indigo-700", "from-blue-500 to-cyan-600", "from-indigo-500 to-blue-800", "from-cyan-500 to-sky-700"],
  test: ["from-amber-400 to-orange-600", "from-orange-400 to-rose-500", "from-yellow-400 to-amber-600", "from-lime-400 to-emerald-600"],
  dinamica: ["from-fuchsia-500 to-pink-600", "from-emerald-400 to-teal-600", "from-rose-400 to-fuchsia-600", "from-teal-400 to-cyan-600"],
};
const TAG = { examen: "Examen", test: "Test", dinamica: "Juego" };
const DOTS = { backgroundImage: "radial-gradient(rgba(255,255,255,.25) 1.5px, transparent 1.5px)", backgroundSize: "16px 16px" };

export function FormatCarousel({ items, value, onPick, kinds }) {
  const ref = useRef(null);
  const scroll = (d) => ref.current?.scrollBy({ left: d * ref.current.clientWidth * 0.8, behavior: "smooth" });
  const idx = {};
  return (
    <div className="relative -mx-1" data-testid="ev-format-carousel">
      <button onClick={() => scroll(-1)} className="hidden sm:flex absolute -left-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white text-navy shadow-xl items-center justify-center hover:scale-110 transition-transform" data-testid="ev-format-prev"><ChevronLeft className="w-5 h-5" /></button>
      <button onClick={() => scroll(1)} className="hidden sm:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white text-navy shadow-xl items-center justify-center hover:scale-110 transition-transform" data-testid="ev-format-next"><ChevronRight className="w-5 h-5" /></button>
      <div ref={ref} className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-px-3 px-3 pt-4 pb-3 scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <AnimatePresence mode="popLayout">
          {items.map((x, n) => {
            const k = BG[x.kind] ? x.kind : "examen";
            idx[k] = (idx[k] ?? -1) + 1;
            const I = (kinds[k] || kinds.examen)[1];
            const on = value === x.key;
            return (
              <motion.button layout key={x.key} onClick={() => onPick(x.key)} data-testid={`ev-format-${x.key}`}
                initial={{ opacity: 0, y: 30, scale: 0.9 }} animate={{ opacity: 1, y: on ? -4 : 0, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }}
                transition={{ delay: Math.min(n * 0.04, 0.4), type: "spring", stiffness: 220, damping: 20 }} whileHover={{ y: -6 }} whileTap={{ scale: 0.97 }}
                className={`snap-start shrink-0 relative overflow-hidden text-left rounded-2xl w-64 sm:w-72 h-44 p-4 bg-gradient-to-br ${BG[k][idx[k] % 4]} text-white shadow-lg ${on ? "ring-4 ring-white shadow-2xl" : "opacity-90 hover:opacity-100"}`}>
                <div className="absolute inset-0" style={DOTS} />
                <I className="absolute -right-4 -bottom-4 w-32 h-32 opacity-20 rotate-12" />
                <div className="relative flex items-center gap-2">
                  <span className="w-9 h-9 rounded-xl bg-white/25 backdrop-blur flex items-center justify-center"><I className="w-5 h-5" /></span>
                  <span className="text-[10px] font-black uppercase tracking-widest bg-black/20 rounded-full px-2 py-0.5">{TAG[k]}</span>
                  {on && <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} className="ml-auto w-7 h-7 rounded-full bg-white text-emerald-600 flex items-center justify-center shadow"><Check className="w-4 h-4" strokeWidth={3} /></motion.span>}
                </div>
                <div className="relative mt-3 text-lg font-black leading-tight break-words drop-shadow">{x.name.replace(/^Dinámica:\s*/, "")}</div>
                <div className="relative mt-1 text-xs leading-snug text-white/90 line-clamp-2">{x.desc}</div>
              </motion.button>
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
}
